import { z } from 'zod';

import {
  ChildOrganizationAccess,
  RoleId,
  RoleType,
  ExportPermission,
  MaximumAssignableVaultPermission,
} from 'backend-client-web';

import { makeProtobufEnumSchema } from '../enumSchemas';
import objectPassthrough from '../objectPassthrough';
import { WithUnknownValuePlaceholder } from '../unknownValuePlaceholder';

interface PersistableRolePermissions {
  // Permissions that require privileged access (at least in some cases)
  adminCanAssignTeamPermissionOther: WithUnknownValuePlaceholder<MaximumAssignableVaultPermission>;
  adminCanAssignTeamPermissionSelf: WithUnknownValuePlaceholder<MaximumAssignableVaultPermission>;
  adminCanChangeSettings: boolean;
  adminCanManageOrganization: boolean;
  adminCanViewForeignPersonalVaults: boolean;
  childOrganizationAccess: WithUnknownValuePlaceholder<ChildOrganizationAccess>;
  exportVaults: WithUnknownValuePlaceholder<ExportPermission>;
  // Permissions that don't required privileged access
  canAccessPersonalVault: boolean;
  canChangePlan: boolean;
  canConnectUsers: boolean;
  canCreateChildOrganizations: boolean;
  canCreateTeams: boolean;
  canViewAuditlog: boolean;
  canViewMonitoring: boolean;
  canViewUsers: boolean;
}

interface PersistableRole {
  id: RoleId;
  roleType: WithUnknownValuePlaceholder<RoleType>;
  customName?: string;

  isPrivileged: boolean;

  permissions: PersistableRolePermissions;

  changedAt: Date;
}

const LegacyPersistableRole = z.object({
  id: z.uuid(),
  roleType: makeProtobufEnumSchema(RoleType, RoleType.UNKNOWN),
  customName: z.string().optional(),

  isPrivileged: z.boolean(),

  adminCanManageOrganization: z.boolean(),

  changedAt: z.date().default(new Date()),
});

const NewPersistableRole = objectPassthrough({
  id: z.uuid(),
  roleType: makeProtobufEnumSchema(RoleType, RoleType.UNKNOWN),
  customName: z.string().optional(),

  isPrivileged: z.boolean(),

  permissions: z.object({
    // NOTE: give all permissions a default,
    // so when new permissions are introduced or permissions are split,
    // the persistable can still be loaded

    // Permissions that require privileged access (at least in some cases)
    adminCanAssignTeamPermissionOther: makeProtobufEnumSchema(
      MaximumAssignableVaultPermission,
      MaximumAssignableVaultPermission.NONE,
    ).default(MaximumAssignableVaultPermission.NONE),
    adminCanAssignTeamPermissionSelf: makeProtobufEnumSchema(
      MaximumAssignableVaultPermission,
      MaximumAssignableVaultPermission.NONE,
    ).default(MaximumAssignableVaultPermission.NONE),
    adminCanChangeSettings: z.boolean().default(false),
    adminCanManageOrganization: z.boolean().default(false),
    adminCanViewForeignPersonalVaults: z.boolean().default(false),
    childOrganizationAccess: makeProtobufEnumSchema(
      ChildOrganizationAccess,
      ChildOrganizationAccess.NONE,
    ).default(ChildOrganizationAccess.NONE),
    exportVaults: makeProtobufEnumSchema(ExportPermission, ExportPermission.NONE).default(
      ExportPermission.NONE,
    ),
    // Permissions that don't required privileged access
    canAccessPersonalVault: z.boolean().default(true),
    canChangePlan: z.boolean().default(false),
    canConnectUsers: z.boolean().default(false),
    canCreateChildOrganizations: z.boolean().default(false),
    canCreateTeams: z.boolean().default(false),
    canViewAuditlog: z.boolean().default(false),
    canViewMonitoring: z.boolean().default(false),
    canViewUsers: z.boolean().default(false),
  }),

  changedAt: z.date().default(new Date()),
});

const PersistableRole = z.union([
  NewPersistableRole,
  LegacyPersistableRole.transform(({ adminCanManageOrganization, ...role }) =>
    NewPersistableRole.parse({
      ...role,
      permissions: { adminCanManageOrganization },
    }),
  ),
]);

export { PersistableRole, type PersistableRolePermissions };
